import React, { useState } from "react";
import { Trophy, Send, Flame, Rocket, ShieldCheck, Heart, MessageCircle, Medal } from "lucide-react";
import { useTranslation, useFormat } from "../../../i18n";
import { useAnalyticsSection } from "../../analytics/hooks";
import { ChartCard, Segmented, Skeleton, ErrorState, RankList, PersonCell, EmptyState } from "../../analytics/primitives";

const LeaderboardsTab = ({ filters }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const [limit, setLimit] = useState(10);
  const q = useAnalyticsSection("leaderboards", filters, { limit });
  const [receivedMetric, setReceivedMetric] = useState("count");
  const [givenMetric, setGivenMetric] = useState("count");
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (q.isLoading || !q.data) return <Skeleton height={400} />;
  const d = q.data;
  const metricToggle = (value, onChange) => <Segmented ariaLabel={t("chart_mode")} value={value} onChange={onChange} options={[{ value: "count", label: t("leaderboards.count") }, { value: "points", label: t("leaderboards.points") }]} />;
  const prevExtra = (row) => (row.previous !== undefined ? t("leaderboards.prev", { value: fmt.number(row.previous) }) : null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Segmented ariaLabel={t("leaderboards.limit")} value={String(limit)} onChange={(v) => setLimit(Number(v))} options={[{ value: "5", label: t("leaderboards.top_n", { count: 5 }) }, { value: "10", label: t("leaderboards.top_n", { count: 10 }) }, { value: "25", label: t("leaderboards.top_n", { count: 25 }) }]} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t("leaderboards.top_recipients")} icon={Trophy} actions={metricToggle(receivedMetric, setReceivedMetric)}>
          <RankList rows={receivedMetric === "count" ? d.received_count : d.received_points} valueLabel={receivedMetric === "count" ? t("table.received") : t("table.points")} renderExtra={receivedMetric === "count" ? prevExtra : undefined} />
        </ChartCard>
        <ChartCard title={t("leaderboards.top_senders")} icon={Send} actions={metricToggle(givenMetric, setGivenMetric)}>
          <RankList rows={givenMetric === "count" ? d.given_count : d.given_points} valueLabel={givenMetric === "count" ? t("table.sent") : t("table.points")} renderExtra={givenMetric === "count" ? prevExtra : undefined} />
        </ChartCard>
        <ChartCard title={t("leaderboards.rising")} subtitle={t("leaderboards.rising_subtitle")} icon={Rocket}>
          <RankList rows={d.rising} renderValue={(r) => `+${fmt.number(r.value)}`} renderExtra={(r) => t("leaderboards.from_to", { from: r.previous, to: r.current })} emptyMessage={t("leaderboards.no_rising")} />
        </ChartCard>
        <ChartCard title={t("leaderboards.type_champions")} subtitle={t("leaderboards.type_champions_subtitle")} icon={Medal}>
          {d.type_champions.length === 0 ? (
            <EmptyState height={140} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.type_champions.map((c) => (
                <li key={c.type.id || c.type.name} className="flex items-center gap-3 py-2">
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 w-32 truncate" title={c.type.name}>
                    {c.type.emoji && !String(c.type.emoji).startsWith("/") && <span aria-hidden="true">{c.type.emoji}</span>}
                    <span className="truncate">{c.type.name}</span>
                  </span>
                  <div className="flex-1 min-w-0">
                    <PersonCell user={c.user} />
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-slate-800">{fmt.number(c.value)}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
        <ChartCard title={t("leaderboards.streaks")} subtitle={t("leaderboards.streaks_subtitle")} icon={Flame}>
          <RankList rows={d.streaks} valueLabel={t("leaderboards.weeks")} renderExtra={(r) => t("leaderboards.best", { value: r.best })} emptyMessage={t("leaderboards.no_streaks")} />
        </ChartCard>
        <ChartCard title={t("leaderboards.approvers")} subtitle={t("leaderboards.approvers_subtitle")} icon={ShieldCheck}>
          <RankList rows={d.approvers} valueLabel={t("leaderboards.decisions")} renderExtra={(r) => t("leaderboards.points_decided", { value: fmt.number(r.points) })} emptyMessage={t("leaderboards.no_approvers")} />
        </ChartCard>
        <ChartCard title={t("leaderboards.reactors")} icon={Heart}>
          <RankList rows={d.reactors} valueLabel={t("kpi.reactions")} />
        </ChartCard>
        <ChartCard title={t("leaderboards.commenters")} icon={MessageCircle}>
          <RankList rows={d.commenters} valueLabel={t("kpi.comments")} />
        </ChartCard>
      </div>
    </div>
  );
};

export default LeaderboardsTab;
